import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './dialog';
import { Button } from './button';
import { AlertTriangle, HelpCircle, Trash2 } from 'lucide-react';

/**
 * Confirmaciones dentro de la página, en lugar de `window.confirm`.
 *
 * Motivo: los diálogos nativos se pueden silenciar. Cuando salen varios seguidos,
 * Chrome ofrece "No permitir más diálogos" y, si el usuario la marca, `confirm()`
 * pasa a devolver false sin preguntar: el botón de aprobar o devolver deja de
 * funcionar y no hay ningún aviso de por qué. Además bloquean el hilo del
 * navegador, no permiten mostrar contexto y en móvil se ven fuera de la página.
 *
 * Uso:
 *   const confirmar = useConfirm();
 *   if (!await confirmar({ titulo, mensaje, confirmLabel, variante })) return;
 */
const ConfirmContext = createContext(null);

const VARIANTES = {
  peligro: { icon: AlertTriangle, color: 'text-red-600', boton: 'bg-red-600 hover:bg-red-700' },
  eliminar: { icon: Trash2, color: 'text-red-600', boton: 'bg-red-600 hover:bg-red-700' },
  advertencia: { icon: AlertTriangle, color: 'text-amber-600', boton: 'bg-amber-600 hover:bg-amber-700' },
  pregunta: { icon: HelpCircle, color: 'text-emerald-600', boton: 'bg-emerald-600 hover:bg-emerald-700' },
};

export const ConfirmProvider = ({ children }) => {
  const [estado, setEstado] = useState(null);
  const resolverRef = useRef(null);

  const confirmar = useCallback((opciones = {}) => {
    const conf = typeof opciones === 'string' ? { mensaje: opciones } : opciones;
    setEstado({
      titulo: conf.titulo || '¿Confirma esta acción?',
      mensaje: conf.mensaje || '',
      detalle: conf.detalle || '',
      confirmLabel: conf.confirmLabel || 'Confirmar',
      cancelLabel: conf.cancelLabel || 'Cancelar',
      variante: conf.variante || 'pregunta',
    });
    return new Promise((resolve) => { resolverRef.current = resolve; });
  }, []);

  const cerrar = (valor) => {
    setEstado(null);
    if (resolverRef.current) {
      resolverRef.current(valor);
      resolverRef.current = null;
    }
  };

  const cfg = VARIANTES[estado?.variante] || VARIANTES.pregunta;
  const Icono = cfg.icon;

  return (
    <ConfirmContext.Provider value={confirmar}>
      {children}
      <Dialog open={!!estado} onOpenChange={(abierto) => { if (!abierto) cerrar(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icono className={`w-5 h-5 ${cfg.color}`} />
              {estado?.titulo}
            </DialogTitle>
            {estado?.mensaje && (
              <DialogDescription className="whitespace-pre-line pt-1">
                {estado.mensaje}
              </DialogDescription>
            )}
          </DialogHeader>
          {estado?.detalle && (
            <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-3 whitespace-pre-line">
              {estado.detalle}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => cerrar(false)} data-testid="confirm-cancelar">
              {estado?.cancelLabel}
            </Button>
            <Button className={cfg.boton} onClick={() => cerrar(true)} data-testid="confirm-aceptar">
              {estado?.confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
};

/**
 * Devuelve una función `confirmar(opciones) => Promise<boolean>`.
 * Si por alguna razón el provider no está montado, cae a window.confirm para no
 * dejar una acción destructiva sin confirmación.
 */
export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  return ctx || (async (opciones) => {
    const conf = typeof opciones === 'string' ? { mensaje: opciones } : (opciones || {});
    // eslint-disable-next-line no-alert
    return window.confirm([conf.titulo, conf.mensaje].filter(Boolean).join('\n\n'));
  });
};

export default ConfirmProvider;
